import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SectionBlock = ({ icon, title, color, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className={`w-8 h-8 bg-${color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon name={icon} size={16} className={`text-${color}-600`} />
        </div>
        <span className="font-semibold text-gray-900 flex-1">{title}</span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-gray-400" />
      </button>
      {open && <div className="px-5 pb-5 border-t border-gray-100">{children}</div>}
    </div>
  );
};

const BulletList = ({ items }) => (
  <ul className="space-y-2 mt-3">
    {(items || [])?.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
        <Icon name="ChevronRight" size={13} className="text-green-500 mt-0.5 flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const BDReportSections = ({ report }) => {
  if (!report) return null;

  const { marketAnalysis, rajukUpdates, newDevelopments, expatTrends } = report || {};

  return (
    <div>
      <SectionBlock icon="TrendingUp" title="Market Analysis & Pricing Forecasts" color="blue" defaultOpen={true}>
        <div className="mt-3 space-y-3">
          {(marketAnalysis?.pricingForecasts || [])?.map((f, i) => (
            <div key={i} className="bg-blue-50 rounded-lg p-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-semibold text-blue-900">{f?.segment}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  f?.forecast?.startsWith('+') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>{f?.forecast}</span>
              </div>
              <p className="text-xs text-gray-600">{f?.analysis}</p>
            </div>
          ))}
          {marketAnalysis?.summary && (
            <p className="text-sm text-gray-700 leading-relaxed mt-2">{marketAnalysis?.summary}</p>
          )}
        </div>
      </SectionBlock>

      <SectionBlock icon="Shield" title="RAJUK Compliance & Regulatory Updates" color="red" defaultOpen={false}>
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <Icon name="ShieldCheck" size={16} className="text-green-600 flex-shrink-0" />
            <p className="text-xs text-green-800 font-medium">রাজধানী উন্নয়ন কর্তৃপক্ষ (RAJUK) — Rajdhani Unnayan Kartripakkha</p>
          </div>
          {(rajukUpdates?.changes || [])?.map((c, i) => (
            <div key={i} className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
              <span className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 ${
                c?.type === 'New' ? 'bg-blue-100 text-blue-700' :
                c?.type === 'Update' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
              }`}>{c?.type}</span>
              <div>
                <p className="text-sm font-medium text-gray-800">{c?.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{c?.description}</p>
                {c?.effectiveDate && <p className="text-xs text-green-600 mt-1">Effective: {c?.effectiveDate}</p>}
              </div>
            </div>
          ))}
          {rajukUpdates?.summary && (
            <p className="text-sm text-gray-700 leading-relaxed mt-3">{rajukUpdates?.summary}</p>
          )}
        </div>
      </SectionBlock>

      <SectionBlock icon="Building2" title="New Residential & Commercial Developments" color="purple" defaultOpen={false}>
        <div className="mt-3 space-y-3">
          {(newDevelopments?.projects || [])?.map((proj, i) => (
            <div key={i} className="border border-purple-100 rounded-lg p-3">
              <div className="flex justify-between items-start mb-1">
                <span className="text-sm font-semibold text-gray-900">{proj?.projectName}</span>
                <span className="text-xs text-purple-600 font-medium">{proj?.developer}</span>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span>📍 {proj?.location}</span>
                <span>💰 From {proj?.startingPrice}</span>
                <span>🏗️ {proj?.handover}</span>
              </div>
              {proj?.highlights && <p className="text-xs text-gray-600 mt-1.5">{proj?.highlights}</p>}
              {proj?.rajukApproved && (
                <span className="inline-flex items-center gap-1 mt-1.5 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                  <Icon name="CheckCircle" size={10} /> RAJUK Approved
                </span>
              )}
            </div>
          ))}
          {newDevelopments?.marketOverview && (
            <p className="text-sm text-gray-700 leading-relaxed">{newDevelopments?.marketOverview}</p>
          )}
        </div>
      </SectionBlock>

      <SectionBlock icon="Globe" title="NRB/Expat Buying Trends & Remittance Impact" color="emerald" defaultOpen={false}>
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <Icon name="Users" size={14} className="text-emerald-600 flex-shrink-0" />
            <p className="text-xs text-emerald-800 font-medium">Non-Resident Bangladeshis (NRB) — প্রবাসী বাংলাদেশি</p>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {(expatTrends?.topDiaspora || [])?.map((n, i) => (
              <div key={i} className="bg-emerald-50 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-800">{n?.country}</span>
                  <span className="text-xs font-bold text-emerald-700">{n?.share}</span>
                </div>
                <div className="w-full bg-emerald-200 rounded-full h-1.5 mt-1.5">
                  <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: n?.share }} />
                </div>
              </div>
            ))}
          </div>
          <BulletList items={expatTrends?.remittanceInsights} />
          {expatTrends?.summary && (
            <p className="text-sm text-gray-700 leading-relaxed mt-3">{expatTrends?.summary}</p>
          )}
        </div>
      </SectionBlock>
    </div>
  );
};

export default BDReportSections;
